import React, { useContext } from 'react';
import { Chip, Spinner, Tooltip } from 'design-system';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { INamedObjectTypes, ITaskPreviewJson } from '@scale/shared/types/lookup';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import TaskShortcuts from 'corp/components/Lookup/sections/subsections/task/shortcuts';
import moment from 'moment';
import { lidarTypes } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import RemoAdminContext from 'components/remoAdmin/remoAdminContext';
import { CustomQuickAction } from './SmartLink';

const TaskPreview: React.FC<{ id: string; customQuickAction?: CustomQuickAction }> = ({
  id,
  customQuickAction,
}) => {
  const remoAdminCtx = useContext(RemoAdminContext);
  const {
    value: task,
    loading,
    error,
  } = useObjectPreview<ITaskPreviewJson>(id, INamedObjectTypes.Task);

  if (error) return <span>Sorry, there was an error</span>;
  if (loading) return <Spinner />;

  const isLidar = (lidarTypes as unknown as TaskType[]).includes(task!.type);
  const isPending = task!.status === 'pending';
  const isComplete = task!.status === 'completed';
  const customerId = task!.user?._id;
  const customerEmail = task!.user?.email;
  const basePathname = window.location.href.includes('/expert')
    ? 'expert/outlieradmin'
    : 'remoadmin';

  const taskHRef = remoAdminCtx.isRemoAdmin
    ? `/${basePathname}/tools/lookup/${id}`
    : `/lookup/${id}`;
  const projectHRef = remoAdminCtx.isRemoAdmin
    ? `/${basePathname}/tools/lookup/${task?.project?._id}`
    : `/qm/queue/${task?.project?._id}`;

  return (
    <div className="task-preview">
      <style jsx>{`
        .task-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
        }

        .task-preview__thing-list > div {
          display: flex;
        }

        .task-preview__thing-list strong {
          color: #666;
          margin-right: 10px;
          min-width: 120px;
          text-align: right;
        }
      `}</style>
      <div className="task-preview__banner">
        <Chip size="small" label={task?.type} />
        <h3 style={{ margin: '0 5px' }}>{id}</h3>
      </div>
      <div className="task-preview__body">
        <div className="task-preview__thing-list">
          <div>
            <strong>Lookup</strong>
            <IsomorphicLink target="_blank" rel="noopener noreferrer" href={taskHRef}>
              {id}{' '}
            </IsomorphicLink>
          </div>
          {!remoAdminCtx.isRemoAdmin && (
            <div>
              <strong>Project</strong>
              <IsomorphicLink target="_blank" rel="noopener noreferrer" href={projectHRef}>
                {task?.project?.name}{' '}
              </IsomorphicLink>
            </div>
          )}
          {!!task?.created_at && (
            <div>
              <strong>Created</strong>
              <Tooltip placement={'top'} title={new Date(task?.created_at).toLocaleString()}>
                <span>{moment(task?.created_at).fromNow()}</span>
              </Tooltip>
            </div>
          )}
          <div>
            <strong>Quick Actions</strong>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <TaskShortcuts
                isLidar={isLidar}
                id={id}
                type="task"
                isPending={isPending}
                isComplete={isComplete}
                customerEmail={customerEmail}
                customerId={customerId}
                customQuickAction={customQuickAction}
                user={task!.user.id}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskPreview;
